/**
 * DebugStepInfo — Per-step detail card showing input, output, timing, error.
 */

import { AlertTriangle, CheckCircle,Clock } from 'lucide-react';
import React from 'react';

import type { IStepResult } from '@/interfaces/IPipeline';
import { cn } from '@/lib/utils';
import { formatDuration, truncate } from '@/lib/utils';

export interface DebugStepInfoProps {
  stepName: string;
  stepIndex: number;
  result: IStepResult | null;
}

export const DebugStepInfo: React.FC<DebugStepInfoProps> = ({
  stepName,
  stepIndex,
  result,
}) => {
  if (!result) {
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
        <div className="font-medium text-foreground mb-1">
          Step {stepIndex + 1}: {stepName}
        </div>
        <span className="italic">Not yet executed</span>
      </div>
    );
  }

  const isError = result.status === 'error';
  const isSkipped = result.status === 'skipped';

  return (
    <div
      className={cn(
        'rounded-lg border p-3 text-sm space-y-2',
        isError
          ? 'border-rose-500/40 bg-rose-500/[0.05]'
          : isSkipped
          ? 'border-yellow-500/40 bg-yellow-500/[0.05]'
          : 'border-emerald-500/40 bg-emerald-500/[0.05]'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="font-medium text-foreground flex items-center gap-1.5">
          {isError ? (
            <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />
          ) : (
            <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
          )}
          Step {stepIndex + 1}: {stepName}
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          {formatDuration(result.duration)}
        </div>
      </div>

      {isError && result.error && (
        <div className="text-xs text-rose-400 font-mono bg-rose-500/[0.08] rounded px-2 py-1">
          {result.error.message}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Input</div>
          <pre className="text-xs font-mono bg-background/50 rounded px-2 py-1 max-h-[80px] overflow-auto whitespace-pre-wrap break-all">
            {truncate(result.input, 500) || '(empty)'}
          </pre>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Output</div>
          <pre className="text-xs font-mono bg-background/50 rounded px-2 py-1 max-h-[80px] overflow-auto whitespace-pre-wrap break-all">
            {truncate(result.output, 500) || '(empty)'}
          </pre>
        </div>
      </div>
    </div>
  );
};
